import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Star, Clock, Users, MapPin, ArrowLeft, Calendar, Facebook, Instagram, ImageIcon, MessageCircle } from 'lucide-react';
import { useNavigation } from '../contexts/NavigationContext';
import { GET_PACKAGE_DETAILS } from '../graphql/queries/package';
import Gallery from '../components/Gallery';
import ReviewList from '../components/ReviewList';
import Itinerary from '../components/Itinerary';
import PackageFormulas from '../components/PackageFormulas';
import PackageCard from '../components/PackageCard';
import DownloadPackageButton from '../components/pdf/DownloadPackageButton';
import AgencyInfoCard from '../components/agency/AgencyInfoCard';
import AirlineBadge from '../components/AirlineBadge';
import Modal from '../components/Modal';
import ClientBookingFormModal from '../components/ClientBookingFormModal';
import LoadingSpinner from '../components/LoadingSpinner';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { TikTokIcon } from '../components/icons/TikTokIcon';
import SocialLinks from '../components/SocialLinks';
import { formatPrice, getDisplayPrice } from '../utils/price';
import { getImageUrl } from '../utils/imageUrl';
import type { 
  Formula, 
  SimilarPackage, 
  PackageDetails,
  Theme
} from '../types/pdf';
import { DEFAULT_THEME } from '../constants/theme';

const parseTheme = (theme: any): Partial<Theme> => {
  try {
    if (!theme) return {};
    
    // Si c'est un objet, nettoyer et valider les valeurs
    if (typeof theme === 'object' && theme !== null) {
      
      // Supprimer __typename et filtrer les valeurs nulles
      const { __typename, ...themeValues } = theme;
      const cleanTheme = Object.entries(themeValues).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key as keyof Theme] = value;
        }
        return acc;
      }, {} as Partial<Theme>);

      return cleanTheme;
    }
    
    // Si c'est une chaîne JSON, la parser
    if (typeof theme === 'string') {
      try {
        const parsedTheme = JSON.parse(theme);
        return parseTheme(parsedTheme); // Réutiliser la logique de nettoyage
      } catch (error) {
        console.error('Erreur de parsing JSON:', error);
        return {};
      }
    }
    
    console.warn('Format de thème non reconnu:', theme);
    return {};
  } catch (error) {
    console.error('Erreur lors du traitement du thème:', error, 'thème reçu:', theme);
    return {};
  }
};

interface PackageDetailPageProps {
  packageId: number;
}

export default function PackageDetailPage({ packageId }: PackageDetailPageProps) {
  const { navigateToAgency, navigateToPackage } = useNavigation();
  const [activeTab, setActiveTab] = useState<'overview' | 'itinerary' | 'gallery' | 'reviews'>('overview');
  const [selectedFormula, setSelectedFormula] = useState<Formula | null>(null);
  const [showReserverModal, setShowReserverModal] = useState(false);

  const { loading, error, data } = useQuery<{ package: PackageDetails }>(
    GET_PACKAGE_DETAILS,
    {
      variables: { id: packageId },
      skip: !packageId,
      onError: (error) => {
        console.error('Erreur Apollo détaillée:', {
          message: error.message,
          graphQLErrors: error.graphQLErrors?.map(err => ({
            message: err.message,
            path: err.path,
            extensions: err.extensions
          })),
          networkError: error.networkError,
          extraInfo: error.extraInfo
        });
      }
    }
  );

  const formatDate = (dateString: string | number | Date | null | undefined) => {
    try {
      if (!dateString) {
        return 'Date à confirmer';
      }
      
      let date: Date;
      
      // Si c'est un timestamp sous forme de chaîne ou de nombre
      if (typeof dateString === 'string' && !isNaN(Number(dateString))) {
        date = new Date(Number(dateString));
      }
      // Si c'est un timestamp (nombre)
      else if (typeof dateString === 'number') {
        date = new Date(dateString);
      }
      // Si c'est déjà un objet Date
      else if (dateString instanceof Date) {
        date = dateString;
      }
      // Si c'est une chaîne de caractères (format date)
      else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        console.error('Date invalide:', dateString);
        return 'Date à confirmer';
      }
      
      return format(date, 'dd MMMM yyyy', { locale: fr });
    } catch (error) {
      console.error('Erreur de formatage de date:', error, 'pour la date:', dateString);
      return 'Date à confirmer';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" color="blue-600" />
      </div>
    );
  }

  if (error || !data?.package) {
    console.error('❌ Erreur ou pas de données:', {
      error: error ? {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError
      } : null,
      data
    });
    return (
        <div className="min-h-screen flex flex-col items-center justify-center">
          <h2 className="text-2xl font-bold mb-4">Package non trouvé</h2>
        <p className="text-gray-600 mb-8">
          {error ? `Erreur: ${error.message}` : 'Le package que vous recherchez n\'existe pas ou a été supprimé.'}
        </p>
          <button
            onClick={navigateToAgency}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Retour à l'accueil
          </button>
        </div>
    );
  }

  const pkg = data.package;
  
  // Traiter le thème quelle que soit sa forme
  const agencyTheme = parseTheme(pkg.agency?.theme);
  
  // Fusionner avec le thème par défaut en s'assurant d'avoir des valeurs valides
  const theme = {
    ...DEFAULT_THEME,
    ...agencyTheme
  };



  // Vérifier que le thème est présent
  if (!pkg.agency?.theme) {
    console.log('Utilisation du thème par défaut car le thème de l\'agence est manquant');
  }

  // Obtenir la prochaine date de départ
  const getNextDeparture = () => {
    if (!pkg.departures || pkg.departures.length === 0) return null;

    const now = new Date();
    const futureDepartures = pkg.departures
      .map(dep => ({
        ...dep,
        departureDate: new Date(dep.departureDate),
        returnDate: new Date(dep.returnDate)
      }))
      .filter(dep => dep.departureDate >= now)
      .sort((a, b) => a.departureDate.getTime() - b.departureDate.getTime());

    return futureDepartures[0] || pkg.departures[0];
  };

  const nextDeparture = getNextDeparture();

  // Mettre à jour le calcul de la durée
  const getDuration = () => {
    if (!nextDeparture) return { days: 0, nights: 0 };

    try {
      // Convertir les timestamps en nombres
      const departureTimestamp = typeof nextDeparture.departureDate === 'string' 
        ? parseInt(nextDeparture.departureDate) 
        : nextDeparture.departureDate;
        
      const returnTimestamp = typeof nextDeparture.returnDate === 'string' 
        ? parseInt(nextDeparture.returnDate) 
        : nextDeparture.returnDate;

      // Créer les objets Date à partir des timestamps
      const departureDate = new Date(departureTimestamp);
      const returnDate = new Date(returnTimestamp);
      
      if (isNaN(departureDate.getTime()) || isNaN(returnDate.getTime())) {
        console.error('Dates invalides après conversion:', {
          departure: {
            original: nextDeparture.departureDate,
            timestamp: departureTimestamp,
            date: departureDate
          },
          return: {
            original: nextDeparture.returnDate,
            timestamp: returnTimestamp,
            date: returnDate
          }
        });
        return { days: 0, nights: 0 };
      }
      
      const days = differenceInDays(returnDate, departureDate);
      return {
        days: Math.max(days, 0),
        nights: Math.max(days - 1, 0)
      };
    } catch (error) {
      console.error('Erreur de calcul de durée:', error, 'pour les dates:', {
        departure: {
          value: nextDeparture.departureDate,
          type: typeof nextDeparture.departureDate
        },
        return: {
          value: nextDeparture.returnDate,
          type: typeof nextDeparture.returnDate
        }
      });
      return { days: 0, nights: 0 };
    }
  };

  const { days, nights } = getDuration();

  const handleBackToAgency = () => {
    navigateToAgency();
  };

  return (
    <div 
      className="min-h-screen flex flex-col" 
      style={{ 
        backgroundColor: theme.backgroundColor || DEFAULT_THEME.backgroundColor 
      }}
    >
      {/* Bouton retour */}
      <div className="fixed top-4 left-4 z-50">
        <button
          onClick={handleBackToAgency}
          style={{ 
            backgroundColor: theme.primaryColor || DEFAULT_THEME.primaryColor,
            color: theme.secondaryColor || DEFAULT_THEME.secondaryColor,
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.2)'
          }}
          className="flex items-center px-5 py-2.5 rounded-xl hover:opacity-90 transition-all duration-200 font-medium"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span>Retour à l&apos;agence</span>
        </button>
      </div>
      
      {/* Hero Section */}
      <div className="relative h-[28rem] overflow-hidden">
        <img src={getImageUrl(pkg.image)} alt={pkg.title} className="w-full h-full object-cover" />
        <div 
          className="absolute inset-0" 
          style={{ 
            background: `linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)`
          }}
        />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 pb-12 w-full">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">{pkg.title}</h1>
            {pkg.airline ? (
              <div className="mb-4">
                <AirlineBadge airline={pkg.airline} tone="hero" />
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-6 text-sm text-white/95">
              {/* Dates */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 shrink-0" />
                  <div>
                    <span className="font-medium">Départ :</span>{' '}
                    {nextDeparture ? (
                      formatDate(nextDeparture.departureDate)
                    ) : (
                      'Dates à venir'
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 shrink-0" />
                  <div>
                    <span className="font-medium">Retour :</span>{' '}
                    {nextDeparture ? (
                      formatDate(nextDeparture.returnDate)
                    ) : (
                      'Dates à venir'
                    )}
                  </div>
                </div>
              </div>
              
              {/* Durée */}
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 shrink-0" />
                <span>{days} jours / {nights} nuits</span>
              </div>

              {/* Lieu de départ */}
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 shrink-0" />
                Départ de {pkg.departure || 'À confirmer'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <nav className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {(['overview', 'itinerary', 'gallery', 'reviews'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{ 
                  color: activeTab === tab ? theme.primaryColor : undefined,
                  borderBottom: activeTab === tab ? `3px solid ${theme.primaryColor}` : '3px solid transparent'
                }}
                className="py-4 px-4 font-medium text-gray-600 hover:text-gray-900 transition-colors -mb-px"
              >
                {{ overview: 'Résumé', itinerary: 'Itinéraire', gallery: 'Galerie', reviews: 'Avis' }[tab]}
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Description</h2>
                  <p className="text-gray-600 leading-relaxed">{pkg.description}</p>
                </article>
                
                <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-5">Points forts</h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {pkg.highlights.map((highlight: string, index: number) => (
                      <li key={index} className="flex items-start gap-3 text-gray-600">
                        <span 
                          className="w-2 h-2 rounded-full mt-2 shrink-0" 
                          style={{ backgroundColor: theme.primaryColor }}
                        />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </article>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-5">Inclus</h3>
                    <ul className="space-y-3">
                      {pkg.included.map((item: string, index: number) => (
                        <li key={index} className="flex items-start gap-3 text-gray-600">
                          <span 
                            className="w-2 h-2 rounded-full mt-2 shrink-0" 
                            style={{ backgroundColor: theme.primaryColor }}
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                  <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-5">Non inclus</h3>
                    <ul className="space-y-3">
                      {pkg.excluded.map((item: string, index: number) => (
                        <li key={index} className="flex items-start gap-3 text-gray-600">
                          <span 
                            className="w-2 h-2 rounded-full mt-2 shrink-0" 
                            style={{ backgroundColor: theme.secondaryColor }}
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                </div>

                <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-5">Formules disponibles</h3>
                  <PackageFormulas
                    formulas={pkg.formulas}
                    selectedFormulaId={selectedFormula?.id}
                    onSelectFormula={setSelectedFormula}
                    theme={theme}
                  />
                </article>
              </div>
            )}

            {activeTab === 'itinerary' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <Itinerary itinerary={pkg.itinerary} />
              </div>
            )}
            {activeTab === 'gallery' && (
              <div className="bg-white rounded-2xl shadow-sm border-2 border-dashed border-gray-200 p-16 text-center">
                <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Galerie</h3>
                <p className="text-gray-500">Prochainement disponible</p>
              </div>
            )}
            {activeTab === 'reviews' && (
              <div className="bg-white rounded-2xl shadow-sm border-2 border-dashed border-gray-200 p-16 text-center">
                <MessageCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Avis</h3>
                <p className="text-gray-500">Prochainement disponible</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-6 sticky top-24">
              {/* Informations de l'agence */}
              <AgencyInfoCard agency={pkg.agency} />

              {/* Actions */}
              <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
                {selectedFormula ? (
                  <>
                    <p className="mb-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      {selectedFormula.discountPercent && selectedFormula.discountPercent > 0 && (
                        <>
                          <span className="text-gray-500 line-through text-sm">
                            {formatPrice(selectedFormula)}
                          </span>
                          <span className="text-gray-500 text-sm">-{selectedFormula.discountPercent}%</span>
                          <span
                            className="text-2xl font-bold tabular-nums"
                            style={{ color: theme.primaryColor }}
                          >
                            {formatPrice({
                              ...selectedFormula,
                              perPersonPrice: selectedFormula.perPersonPrice 
                                ? selectedFormula.perPersonPrice * (1 - (selectedFormula.discountPercent || 0) / 100)
                                : null,
                              roomPrices: Object.entries(selectedFormula.roomPrices).reduce((acc, [key, value]) => ({
                                ...acc,
                                [key]: value * (1 - (selectedFormula.discountPercent || 0) / 100)
                              }), {} as typeof selectedFormula.roomPrices)
                            })}
                          </span>
                        </>
                      )}
                      {(!selectedFormula.discountPercent || selectedFormula.discountPercent === 0) && (
                        <span
                          className="text-2xl font-bold tabular-nums"
                          style={{ color: theme.primaryColor }}
                        >
                          {formatPrice(selectedFormula)}
                        </span>
                      )}
                    </p>

                    <button 
                      type="button"
                      onClick={() => setShowReserverModal(true)}
                      style={{ 
                        backgroundColor: theme.secondaryColor,
                        color: theme.primaryColor,
                      }}
                      className="w-full py-3.5 rounded-xl font-medium hover:opacity-90 transition mb-4"
                    >
                      Réserver cette formule
                    </button>
                  </>
                ) : (
                  <div className="text-center text-gray-500 mb-4 text-sm">
                    Sélectionnez une formule pour réserver
                  </div>
                )}

                <DownloadPackageButton
                  package={pkg}
                  agency={pkg.agency}
                  className="mt-4"
                  theme={theme}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Packages similaires */}
        {pkg.similarPackages && pkg.similarPackages.length > 0 && (
          <section className="mt-16 pt-12 border-t border-gray-200">
            <h2 
              className="text-2xl font-bold mb-2" 
              style={{ color: theme.textColor || DEFAULT_THEME.textColor }}
            >
              Packages similaires
            </h2>
            <div 
              className="h-1 w-16 rounded-full mb-8" 
              style={{ backgroundColor: theme.primaryColor }}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {pkg.similarPackages.map(similarPackage => (
                <PackageCard 
                  key={similarPackage.id} 
                  package={similarPackage}
                  primaryColor={theme.primaryColor}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Social Links */}
      <SocialLinks
        facebook={pkg.agency.facebook}
        instagram={pkg.agency.instagram}
        tiktok={pkg.agency.tiktok}
        primaryColor={theme.primaryColor}
      />

      <ClientBookingFormModal
        isOpen={showReserverModal}
        onClose={() => setShowReserverModal(false)}
        pkg={pkg}
        selectedFormula={selectedFormula}
        theme={{
          primaryColor: theme.primaryColor,
          secondaryColor: theme.secondaryColor,
        }}
      />
    </div>
  );
}